import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGym } from '../contexts/GymContext';
import { GymSelector } from './GymSelector';
import { AlvoLogo } from './AlvoLogo';
import { getAreaByPath, getItemByPath, getAreaEntryPath } from '../config/areas';
import { 
  LogOut,
  Menu,
  X,
  Shield,
  ArrowLeft,
  ChevronRight
} from 'lucide-react';

// Admin emails - único administrador do sistema
const ADMIN_EMAILS = ['marciojr2444@gmail.com'];

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedGym, loading: gymLoading } = useGym();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is admin
  const isAdmin = user && (ADMIN_EMAILS.includes(user.email) || user.is_admin);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Navegação contextual: a lateral mostra apenas as subáreas da macroárea atual.
  const currentArea = getAreaByPath(location.pathname);
  const menuItems = currentArea?.items || [];
  const crumb = getItemByPath(location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--app-background)', color: 'var(--app-text-primary)' }}>
      {/* Mobile Header Bar */}
      <div 
        className="lg:hidden fixed top-0 left-0 right-0 h-16 z-40 flex items-center justify-between px-4 border-b"
        style={{ 
          backgroundColor: 'var(--app-background)', 
          borderColor: 'var(--app-border)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
        }}
      >
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2.5 rounded-xl border transition-colors active:scale-95"
          style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)', color: 'var(--color-primary)' }}
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        
        <div className="flex items-center gap-2">
          <AlvoLogo size={32} />
          <span className="text-xl font-heading font-black uppercase" style={{ color: 'var(--app-text-primary)' }}>Alvo</span>
        </div>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-72 lg:w-64 border-r flex flex-col z-50 transform transition-all duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          backgroundColor: 'var(--app-background)',
          borderColor: 'var(--app-border)',
          boxShadow: sidebarOpen ? '4px 0 30px rgba(0, 0, 0, 0.6)' : '4px 0 24px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Close button for mobile - inside sidebar */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 rounded-lg transition-colors"
          style={{ color: 'var(--app-text-secondary)' }}
        >
          <X size={20} />
        </button>

        <div className="p-6 border-b" style={{ borderColor: 'var(--app-border)' }}>
          <Link to="/areas" className="flex items-center gap-3 mb-2" data-testid="sidebar-logo-home">
            <AlvoLogo size={40} />
            <h1 className="text-3xl font-heading font-black uppercase tracking-tight" style={{ color: 'var(--app-text-primary)' }}>
              Alvo
            </h1>
          </Link>
          <p className="text-xs uppercase tracking-widest font-medium" style={{ color: 'var(--app-text-secondary)' }}>Performance Empresarial</p>
        </div>

        {/* Gym Selector */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--app-border)' }}>
          {gymLoading ? (
            <div className="text-center py-3 text-sm" style={{ color: 'var(--app-text-secondary)' }}>Carregando unidades...</div>
          ) : !selectedGym ? (
            <div className="text-center py-3 text-sm" style={{ color: 'var(--app-text-secondary)' }}>Nenhuma unidade encontrada</div>
          ) : (
            <GymSelector />
          )}
        </div>

        {/* Voltar ao hub + nome da macroárea atual */}
        <div className="px-4 pt-4">
          <Link
            to="/areas"
            data-testid="nav-todas-areas"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-colors text-sm font-medium"
            style={{ color: 'var(--app-text-secondary)', border: '1px solid var(--app-border)' }}
            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--color-primary) 40%, transparent)'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--app-text-secondary)'; e.currentTarget.style.borderColor = 'var(--app-border)'; }}
          >
            <ArrowLeft size={16} />
            Todas as áreas
          </Link>
          {currentArea && (
            <p className="mt-4 px-1 text-[11px] uppercase tracking-widest font-semibold" style={{ color: 'var(--color-primary)' }} data-testid="sidebar-area-title">
              {currentArea.label}
            </p>
          )}
        </div>

        <nav className="flex-1 p-4 pt-2 space-y-1 overflow-y-auto custom-scrollbar" data-testid="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-4 py-4 lg:py-3.5 rounded-xl transition-all duration-300 group relative overflow-hidden active:scale-[0.98]"
                style={isActive ? {
                  background: `linear-gradient(to right, color-mix(in srgb, var(--color-primary) 10%, transparent), transparent)`,
                  color: 'var(--color-primary)',
                  border: `1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)`,
                  boxShadow: '0 0 20px color-mix(in srgb, var(--color-primary) 10%, transparent)'
                } : {
                  color: 'var(--app-text-secondary)',
                  border: '1px solid transparent'
                }}
              >
                {isActive && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full" style={{ backgroundColor: 'var(--color-primary)' }} />
                )}
                <Icon size={20} style={{ color: isActive ? 'var(--color-primary)' : 'inherit' }} className="group-hover:opacity-80 transition-colors duration-300" />
                <span className="font-medium text-base lg:text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: 'var(--app-border)' }}>
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-black font-bold text-sm" style={{ background: `linear-gradient(to bottom right, var(--color-primary), var(--color-secondary))` }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: 'var(--app-text-primary)' }}>{user?.name}</p>
              <p className="text-xs truncate" style={{ color: 'var(--app-text-secondary)' }}>{user?.email}</p>
            </div>
          </div>
          
          {/* Admin Panel Link - Only visible for admins */}
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-3 px-4 py-3 mb-2 rounded-xl transition-all duration-300 group"
              style={{
                background: 'linear-gradient(to right, rgba(168, 201, 74, 0.1), rgba(90, 172, 184, 0.1))',
                border: '1px solid rgba(168, 201, 74, 0.3)',
                color: 'var(--color-primary)'
              }}
            >
              <Shield size={18} />
              <span className="font-medium text-sm">Painel Admin</span>
            </Link>
          )}
          
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-3 lg:py-2.5 rounded-xl transition-all duration-300 border border-transparent active:scale-[0.98]"
            style={{ color: 'var(--app-text-secondary)' }}
            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-danger)'; e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-danger) 5%, transparent)'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--app-text-secondary)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <LogOut size={18} />
            <span className="font-medium">Sair</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-10">
          {crumb && (
            <nav className="flex items-center gap-1.5 mb-5 text-sm flex-wrap" data-testid="breadcrumb">
              <Link to="/areas" className="transition-colors hover:opacity-80" style={{ color: 'var(--app-text-secondary)' }} data-testid="breadcrumb-home">
                Áreas
              </Link>
              <ChevronRight size={14} style={{ color: 'var(--app-text-secondary)' }} />
              <Link
                to={getAreaEntryPath(crumb.area)}
                className="transition-colors hover:opacity-80 font-medium"
                style={{ color: crumb.area.color }}
                data-testid="breadcrumb-area"
              >
                {crumb.area.label}
              </Link>
              <ChevronRight size={14} style={{ color: 'var(--app-text-secondary)' }} />
              <span className="font-semibold" style={{ color: 'var(--app-text-primary)' }} data-testid="breadcrumb-current">
                {crumb.item.label}
              </span>
            </nav>
          )}
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};